import { doGet, myFetch, handleResp } from 'src/api/client';

import {
  mergeRefTableBundlesFromManifest,
  normalizeEuInterfaceLanguage,
  normalizeEuRefTableTypesForFetch,
  normalizeRitaRefTableTypesForFetch,
  refTablesUploadDateString
} from 'src/services/referenceTables.js';
import { deepcopy } from 'src/utils/object';

import { EU_REF_CACHE_NAMES_BY_TYPE, RITA_REF_CACHE_NAMES_BY_TYPE } from 'src/constants/referenceTables';
/**
 * An object to be used in Notes; containing the following default values
 */
const NotesListParams = {
  id_node: null,
  language: null,
  include_parent_notes: true,
  type_note: 'ALL',
  enrich_text: true,
  searched_text: null,
  searched_date: null
};

export function getNotesListParams() {
  return deepcopy(NotesListParams);
}

/**
 * An object to be used in Notes search; containing the following default values
 */
const NotesSearchParams = {
  language: null,
  enrich_text: true,
  text_to_search: null,
  highlight_text: true
};

export function getNotesSearchParams() {
  return deepcopy(NotesSearchParams);
}

/**
 * This function will request to Customs Bridge API a list of importers notices related to input parameters
 * @param {String} nomencCode OPTIONAL The nomenclature code to filter on. All BTI related to other codes will be removed from results
 * @returns {Promise} A promise containing the server response
 */
export async function reqGetNotices(nomencCode) {
  return doGet(`notices`, { nomencCode });
}

/**
 * This function will request the list of all available nomenclatures for a specific area and language.
 * @param {String} area The customs area for which we want the nomenclature
 * @param {String} language the language in which we want the nomenclature
 * @returns the list of all available nomenclatures
 */
export async function reqNomencList(area, language) {
  return doGet(`nomenclature/list`, { area, language });
}

/**
 * @param {string} linkNomenc the link in which the nomenclature is stored on the swift server
 * @returns {Object} the result of the request, or the request with error details in case of error
 */
export async function reqNomenc(linkNomenc) {
  // TODO: Loïc only: When the nomenc data is updated daily, maybe implement a deletion functionnality (maybe via an interface)
  // https://developer.mozilla.org/fr/docs/Web/API/CacheStorage/delete
  try {
    const cache = await caches.open('nomencs');
    const cachedResponse = await cache.match(linkNomenc);

    if (cachedResponse?.status === 200) {
      return handleResp(cachedResponse);
    } else {
      const response = await fetch(linkNomenc); // This has to stay Fetch and not myFetch, because we don't want to send the JWT token
      if (response.ok) await cache.put(linkNomenc, response.clone());
      return handleResp(response);
    }
  } catch (error) {
    console.error('Error:', error);
    return { error: 'Internal error while getting / caching nomenclature' };
  }
}

/**
 * Shared: manifest → merge bundles, with Cache Storage per bundle (`/data/{path}`).
 *
 * @param {object} opts
 * @param {string} opts.manifestPath path passed to `myFetch` (incl. query)
 * @param {string[]|undefined} opts.requestedTypes
 * @param {(types: string[]|undefined) => string[]} opts.normalizeTypes
 * @param {Record<string, string>} opts.cacheNamesByType
 * @param {string} opts.dataLogLabel console context, e.g. `RITA data` / `EU ref data`
 * @param {string} opts.internalErrorMessage
 * @returns {Promise<Object>}
 */
async function reqGetRefTablesViaManifest({
  manifestPath,
  requestedTypes,
  normalizeTypes,
  cacheNamesByType,
  dataLogLabel,
  internalErrorMessage
}) {
  try {
    const manifestResponse = await myFetch(manifestPath);
    const manifest = await handleResp(manifestResponse);

    if (!manifestResponse.ok || manifest?.error) {
      return manifest;
    }

    const documents = manifest.documents || [];
    const typesToFetch = normalizeTypes(requestedTypes);

    return await mergeRefTableBundlesFromManifest(
      documents,
      typesToFetch,
      cacheNamesByType,
      async ({ type, dataURL, cacheName }) => {
        try {
          const cache = await caches.open(cacheName);
          const request = new Request(dataURL);
          const cachedResponse = await cache.match(request);
          if (cachedResponse?.status === 200) {
            return await handleResp(cachedResponse.clone());
          }
          const dataResponse = await fetch(dataURL);
          const subset = await handleResp(dataResponse.clone());
          if (dataResponse.ok && !subset?.error) {
            try {
              await cache.put(request, dataResponse.clone());
            } catch (cacheError) {
              console.error(`Error while caching ${dataLogLabel} for type ${type}:`, cacheError);
            }
          }
          return subset;
        } catch (cacheOrNetworkError) {
          console.error(`Error while getting / caching ${dataLogLabel} for type ${type}:`, cacheOrNetworkError);
          return null;
        }
      }
    );
  } catch (error) {
    console.error('Error:', error);
    return { error: internalErrorMessage };
  }
}

/**
 * RITA reference tables: manifest `rita/ref_tables` then `/data/{path}` per type (Cache Storage per bundle).
 *
 * @param {string[]} [requestedTypes] ['delta_i', 'delta_e', 'delta_t', 'common']
 * @returns {Promise<Object>} merged payload or error object
 */
export async function reqGetRitaRefTables(requestedTypes) {
  const today = refTablesUploadDateString();
  return reqGetRefTablesViaManifest({
    manifestPath: `rita/ref_tables?upload_date=${today}`,
    requestedTypes,
    normalizeTypes: normalizeRitaRefTableTypesForFetch,
    cacheNamesByType: RITA_REF_CACHE_NAMES_BY_TYPE,
    dataLogLabel: 'RITA data',
    internalErrorMessage: 'Internal error while getting / caching teledecdata'
  });
}

/**
 * EU reference tables: manifest `europa/ref_tables` then `/data/{path}` per bundle.
 *
 * @param {string} language interface language (2-letter)
 * @param {string[]} [requestedTypes] subset of ['delta_t', 'common']
 * @returns {Promise<Object>} merged JSON or error object
 */
export async function reqGetEuRefTables(language, requestedTypes) {
  const today = refTablesUploadDateString();
  const lang = normalizeEuInterfaceLanguage(language);
  return reqGetRefTablesViaManifest({
    manifestPath: `europa/ref_tables?upload_date=${today}&language=${encodeURIComponent(lang)}`,
    requestedTypes,
    normalizeTypes: normalizeEuRefTableTypesForFetch,
    cacheNamesByType: EU_REF_CACHE_NAMES_BY_TYPE,
    dataLogLabel: 'EU ref data',
    internalErrorMessage: 'Internal error while getting / caching euRefTables'
  });
}

type ClassifyStreamCallbacks = {
  onStep?: (step: string) => void;
  onExtracted?: (description: string) => void;
};

type ClassifyStreamState = {
  result: unknown;
  streamError: string | null;
  sawEmpty: boolean;
};

class ClassifyResponseError extends Error {
  readonly status: number;

  constructor(private readonly response: Response) {
    super(`Classification request failed with status ${response.status}`);
    this.name = 'ClassifyResponseError';
    this.status = response.status;
  }

  json() {
    return this.response.clone().json();
  }
}

async function consumeClassifyStream(response: Response, callbacks: ClassifyStreamCallbacks) {
  if (!response.body) throw new ClassifyResponseError(response);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const state: ClassifyStreamState = { result: null, streamError: null, sawEmpty: false };
  let buffer = '';

  function applyEvent(event: Record<string, unknown>) {
    if (event.type === 'step' && typeof event.step === 'string') callbacks.onStep?.(event.step);
    if (event.type === 'extracted' && typeof event.description === 'string') {
      callbacks.onExtracted?.(event.description);
    }
    if (event.type === 'result') state.result = event.data ?? null;
    if (event.type === 'empty') state.sawEmpty = true;
    if (event.type === 'error') {
      state.streamError = typeof event.detail === 'string' ? event.detail : 'classify_error_generic';
    }
  }

  function consumeLine(line: string) {
    const trimmedLine = line.trim();
    if (!trimmedLine) return;
    try {
      applyEvent(JSON.parse(trimmedLine));
    } catch {
      // Ignore malformed or incomplete stream chunks.
    }
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      lines.forEach(consumeLine);
    }
    buffer += decoder.decode();
    consumeLine(buffer);
  } finally {
    reader.releaseLock();
  }

  if (state.streamError) {
    const error = new Error(state.streamError) as Error & { detail: string };
    error.detail = state.streamError;
    throw error;
  }
  return state.sawEmpty ? null : state.result;
}

export async function reqClassify(
  params: Record<string, unknown>,
  files: File[] | FileList | null = null,
  callbacks: ClassifyStreamCallbacks = {}
) {
  const fileList = files ? Array.from(files).filter(Boolean) : [];
  let body: FormData | string;
  const headers: Record<string, string> = {};

  if (fileList.length === 0) {
    body = JSON.stringify(params);
    headers['Content-Type'] = 'application/json';
  } else {
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]) => {
      if (value == null) return;
      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });
    fileList.forEach((file) => formData.append('files', file));
    body = formData;
  }

  const response = await myFetch('classify', { method: 'POST', body, headers });
  if (response.status === 401) return handleResp(response);
  if (![200, 201].includes(response.status)) throw new ClassifyResponseError(response);

  const contentType = (response.headers.get('content-type') ?? '').split(';')[0];
  if (contentType === 'application/x-ndjson' || contentType === 'application/ndjson') {
    return consumeClassifyStream(response, callbacks);
  }
  return handleResp(response);
}

export async function reqGetMeasures(idNode, code, language, country) {
  return doGet(`duties`, { idNode, code, language, country });
}

export async function reqSearchAntiDumping(text, language) {
  return doGet(`duties/anti-dumping`, { search_company_name: text, language });
}

export async function reqGetDUGbyNomencID(nomencID, language) {
  return doGet(`dug/text/${nomencID}`, { language });
}

export async function reqGetNomencID(nomencCode) {
  return doGet(`nomenclature/convert`, { nomencCode });
}

export async function reqSearchNomenc(params) {
  return doGet(`nomenclature/search`, params);
}

export async function reqMeursingRatesByCode(code, language) {
  return doGet(`duties/meursing/${code}`, { language });
}

export async function reqMeursingRatesByComposition(params) {
  return doGet(`duties/meursing`, params);
}

export async function reqGetCodeNotes(params) {
  return doGet(`notes/get_specific_notes`, params);
}

export async function reqNoteSearch(params) {
  return doGet(`notes/search_in_notes`, params);
}

export async function reqGetNomenclatureUpdates(fromDate, language) {
  return doGet(`nomenclature/get_news`, { fromDate, language });
}

export async function reqGetNomenclatureById(idNomenclature, language, searchedDate) {
  return doGet(`nomenclature/${idNomenclature}`, { language, searchedDate });
}
